import React, { useMemo, useState } from 'react';
import { Search, X, Check, Repeat } from 'lucide-react';

const MAX_SUBSTITUTES = 2;

/**
 * Picker for exercise substitutes. Selects up to N library exercises.
 * Props:
 * - selectedIds: string[]
 * - onChange: (ids: string[]) => void
 * - library: ExerciseLibrary[] (id, name, category, youtube_url)
 * - currentExerciseName?: string   (used to exclude self from suggestions)
 * - max?: number (default 2)
 */
export const SubstitutePicker = ({
  selectedIds = [],
  onChange,
  library = [],
  currentExerciseName = '',
  max = MAX_SUBSTITUTES,
}) => {
  const [search, setSearch] = useState('');
  const [showList, setShowList] = useState(false);

  const selectedItems = useMemo(
    () => selectedIds.map((id) => library.find((l) => l.id === id)).filter(Boolean),
    [selectedIds, library]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return library
      .filter((l) => !selectedIds.includes(l.id))
      .filter((l) => (currentExerciseName ? l.name.toLowerCase() !== currentExerciseName.toLowerCase() : true))
      .filter((l) => (term ? l.name.toLowerCase().includes(term) : true))
      .slice(0, 30);
  }, [library, search, selectedIds, currentExerciseName]);

  const addSub = (id) => {
    if (selectedIds.length >= max) return;
    onChange([...selectedIds, id]);
    setSearch('');
    setShowList(false);
  };

  const removeSub = (id) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const canAdd = selectedIds.length < max;

  return (
    <div className="bg-zinc-900/60 border border-white/10 rounded-xl p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Repeat className="w-4 h-4 text-neon-lime" />
        <span className="text-white font-bold text-sm">Exercícios Substitutos</span>
        <span className="text-zinc-500 text-xs ml-auto">
          {selectedIds.length}/{max}
        </span>
      </div>

      {/* Selected pills */}
      {selectedItems.length > 0 && (
        <div className="space-y-1.5">
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 bg-neon-lime/10 border border-neon-lime/30 rounded-lg px-3 py-2"
              data-testid={`substitute-selected-${item.id}`}
            >
              <Check className="w-4 h-4 text-neon-lime flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-bold truncate">{item.name}</p>
                {item.category && (
                  <p className="text-zinc-400 text-[11px] truncate">{item.category}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeSub(item.id)}
                className="w-7 h-7 rounded-full bg-zinc-800 hover:bg-red-500/20 text-zinc-400 hover:text-red-400 flex items-center justify-center flex-shrink-0"
                data-testid={`substitute-remove-${item.id}`}
                title="Remover"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search / add */}
      {canAdd && (
        <div className="relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowList(true);
            }}
            onFocus={() => setShowList(true)}
            placeholder="Pesquisar exercício substituto..."
            className="w-full bg-zinc-950 border border-zinc-800 text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-neon-lime/50"
            data-testid="substitute-search-input"
          />
          {showList && (search.trim() || filtered.length > 0) && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-56 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl">
              {filtered.length === 0 ? (
                <p className="text-zinc-500 text-xs text-center py-3">
                  Nenhum exercício encontrado
                </p>
              ) : (
                filtered.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => addSub(l.id)}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-800 transition-colors border-b border-white/5 last:border-b-0"
                    data-testid={`substitute-option-${l.id}`}
                  >
                    <p className="text-white text-sm font-bold truncate">{l.name}</p>
                    {l.category && (
                      <p className="text-zinc-500 text-[11px] truncate">{l.category}</p>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {!canAdd && (
        <p className="text-zinc-500 text-xs italic">
          Limite de {max} substitutos atingido. Remova um para adicionar outro.
        </p>
      )}
    </div>
  );
};
